import { DomainError } from "./errors";

/**
 * SQL somente leitura do Copiloto (T1.14, `run_readonly_sql`). Aceita um subconjunto de SELECT sobre um catálogo de
 * visões e devolve SQL REESCRITO a partir da AST: nunca executa o texto recebido. Cada visão vira a subconsulta do
 * servidor (com empresa e carteira já filtradas), identificadores saem conferidos e entre aspas, textos viram
 * parâmetros. Não há como escrever, chamar PRAGMA/ATTACH, subconsulta, CTE, UNION nem função fora da lista.
 */

export type SqlColumnType = "text" | "integer" | "real";
export type SqlViewDef = {
  /** SELECT do servidor que produz a visão (exatamente as `columns`), com `?` para `params`. */
  source: string;
  params: readonly (string | number)[];
  columns: Readonly<Record<string, SqlColumnType>>;
  /** Chave primária da visão: coluna ÚNICA por linha (ausente em visões agregadas, que não são alvo de JOIN). */
  pk?: string;
  /** Chaves estrangeiras: coluna desta visão → visão cuja `pk` ela referencia. */
  fks?: Readonly<Record<string, string>>;
};
export type SqlCatalog = Readonly<Record<string, SqlViewDef>>;
export type CompiledSql = {
  sql: string;
  params: (string | number)[];
  /** Nome de cada coluna do resultado, na ordem. */
  columns: string[];
  /** LIMIT aplicado (o pedido, cortado no teto, ou o padrão). */
  limit: number;
  views: string[];
};

export const SQL_MAX_CHARS = 4000;
export const SQL_MAX_TOKENS = 1000;
export const SQL_MAX_DEPTH = 30;
export const SQL_MAX_COLUMNS = 30;
/**
 * Revisão de segurança T1.14: o SQLite do DO não tem timeout nem progress handler, então o custo é limitado pela
 * estrutura — até 2 JOIN, cada um de chave estrangeira para chave primária, e no máximo um deles "um para muitos".
 */
export const SQL_MAX_JOINS = 2;
export const SQL_MAX_LITERALS = 60;
export const SQL_MAX_IN = 50;
export const SQL_DEFAULT_LIMIT = 100;
export const SQL_MAX_LIMIT = 500;
export const SQL_MAX_OFFSET = 10_000;
/** O SQLite do Durable Object aceita até 100 parâmetros por consulta. */
const MAX_PARAMS = 99;
const LABEL_MAX = 60;

const bad = (message: string): never => {
  throw new DomainError("invalid_sql", message);
};

// ---------- tokens ----------

export type SqlToken = { t: "kw" | "id" | "num" | "str" | "op" | "eof"; v: string };

const KEYWORDS = new Set([
  "SELECT",
  "DISTINCT",
  "ALL",
  "FROM",
  "WHERE",
  "GROUP",
  "BY",
  "HAVING",
  "ORDER",
  "LIMIT",
  "OFFSET",
  "JOIN",
  "LEFT",
  "INNER",
  "OUTER",
  "ON",
  "AS",
  "AND",
  "OR",
  "NOT",
  "IN",
  "IS",
  "NULL",
  "LIKE",
  "BETWEEN",
  "CASE",
  "WHEN",
  "THEN",
  "ELSE",
  "END",
  "CAST",
  "ASC",
  "DESC",
  "UNION",
  "EXCEPT",
  "INTERSECT",
  "WITH",
  "INSERT",
  "UPDATE",
  "DELETE",
  "REPLACE",
  "DROP",
  "ALTER",
  "CREATE",
  "PRAGMA",
  "ATTACH",
  "DETACH",
  "VACUUM",
  "REINDEX",
  "ANALYZE",
  "BEGIN",
  "COMMIT",
  "ROLLBACK",
  "SAVEPOINT",
  "RELEASE",
  "EXISTS",
  "VALUES",
  "INTO",
  "SET",
  "ESCAPE",
  "GLOB",
  "REGEXP",
  "MATCH",
  "COLLATE",
]);
const OPS2 = ["<>", "!=", "<=", ">=", "==", "||"];
const OPS1 = "=<>+-*/%(),.;";

export function tokenizeSql(src: string): SqlToken[] {
  const out: SqlToken[] = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    const ch = src[i]!;
    if (/\s/.test(ch)) {
      i++;
    } else if (src.startsWith("--", i)) {
      const end = src.indexOf("\n", i);
      i = end < 0 ? n : end + 1;
    } else if (src.startsWith("/*", i)) {
      const end = src.indexOf("*/", i + 2);
      if (end < 0) bad("Comentário sem fim.");
      i = end + 2;
    } else if (ch === "'") {
      let v = "";
      let j = i + 1;
      for (;;) {
        if (j >= n) bad("Texto entre aspas simples sem fim.");
        if (src[j] === "'") {
          if (src[j + 1] === "'") {
            v += "'";
            j += 2;
            continue;
          }
          break;
        }
        v += src[j];
        j++;
      }
      out.push({ t: "str", v });
      i = j + 1;
    } else if (ch === '"') {
      const end = src.indexOf('"', i + 1);
      if (end < 0) bad("Nome entre aspas duplas sem fim.");
      const v = src.slice(i + 1, end);
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(v)) bad("Nome entre aspas duplas inválido.");
      out.push({ t: "id", v: v.toLowerCase() });
      i = end + 1;
    } else if (/[0-9]/.test(ch)) {
      const m = /^[0-9]+(\.[0-9]+)?([eE][+-]?[0-9]+)?/.exec(src.slice(i))!;
      out.push({ t: "num", v: m[0] });
      i += m[0].length;
    } else if (/[A-Za-z_]/.test(ch)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!;
      const up = m[0].toUpperCase();
      out.push(KEYWORDS.has(up) ? { t: "kw", v: up } : { t: "id", v: m[0].toLowerCase() });
      i += m[0].length;
    } else {
      const two = src.slice(i, i + 2);
      if (OPS2.includes(two)) {
        out.push({ t: "op", v: two });
        i += 2;
      } else if (OPS1.includes(ch)) {
        out.push({ t: "op", v: ch });
        i++;
      } else {
        bad(`Caractere não aceito: ${ch}`);
      }
    }
  }
  out.push({ t: "eof", v: "" });
  return out;
}

// ---------- AST ----------

type Expr =
  | { k: "num"; v: string }
  | { k: "str"; v: string }
  | { k: "null" }
  | { k: "col"; t?: string; c: string }
  | { k: "paren"; e: Expr }
  | { k: "un"; op: "-" | "+" | "NOT"; e: Expr }
  | { k: "bin"; op: string; l: Expr; r: Expr }
  | { k: "isnull"; e: Expr; not: boolean }
  | { k: "in"; e: Expr; not: boolean; list: Expr[] }
  | { k: "like"; e: Expr; not: boolean; p: Expr }
  | { k: "between"; e: Expr; not: boolean; lo: Expr; hi: Expr }
  | { k: "case"; base?: Expr; whens: [Expr, Expr][]; els?: Expr }
  | { k: "cast"; e: Expr; type: string }
  | { k: "fn"; name: string; distinct: boolean; star: boolean; args: Expr[] };

type Item = { k: "star"; t?: string } | { k: "expr"; e: Expr; alias?: string };
type Ref = { view: string; alias: string };
type Join = { left: boolean; ref: Ref; on: Expr };
type Select = {
  distinct: boolean;
  items: Item[];
  from: Ref;
  joins: Join[];
  where?: Expr;
  groupBy: Expr[];
  having?: Expr;
  orderBy: { e: Expr; dir?: "ASC" | "DESC" }[];
  limit?: number;
  offset?: number;
};

/** Funções aceitas: [mínimo, máximo] de argumentos e se é agregação (DISTINCT só nelas). */
const FUNCTIONS: Readonly<Record<string, readonly [number, number, boolean]>> = {
  count: [0, 1, true],
  sum: [1, 1, true],
  total: [1, 1, true],
  avg: [1, 1, true],
  min: [1, 1, true],
  max: [1, 1, true],
  abs: [1, 1, false],
  round: [1, 2, false],
  coalesce: [2, 8, false],
  ifnull: [2, 2, false],
  nullif: [2, 2, false],
  lower: [1, 1, false],
  upper: [1, 1, false],
  length: [1, 1, false],
  trim: [1, 2, false],
  substr: [2, 3, false],
  instr: [2, 2, false],
  date: [1, 4, false],
  strftime: [2, 4, false],
  julianday: [1, 3, false],
};
const CAST_TYPES = new Set(["INTEGER", "REAL", "TEXT", "NUMERIC"]);
const CMP = new Set(["=", "==", "!=", "<>", "<", "<=", ">", ">="]);

class Parser {
  private i = 0;
  private depth = 0;
  literals = 0;
  constructor(private readonly toks: SqlToken[]) {}

  private peek(o = 0): SqlToken {
    return this.toks[Math.min(this.i + o, this.toks.length - 1)]!;
  }
  /** Só depois de conferir o token com `peek` (nunca no fim). */
  private next(): SqlToken {
    return this.toks[this.i++]!;
  }
  private isKw(v: string, o = 0) {
    const t = this.peek(o);
    return t.t === "kw" && t.v === v;
  }
  private isOp(v: string) {
    const t = this.peek();
    return t.t === "op" && t.v === v;
  }
  private acceptKw(v: string) {
    if (!this.isKw(v)) return false;
    this.i++;
    return true;
  }
  private acceptOp(v: string) {
    if (!this.isOp(v)) return false;
    this.i++;
    return true;
  }
  private unexpected(what: string): never {
    const t = this.peek();
    return t.t === "eof"
      ? bad(`Fim inesperado da consulta: esperado ${what}.`)
      : bad(`Trecho inesperado perto de "${t.v}": esperado ${what}.`);
  }
  private expectKw(v: string) {
    if (!this.acceptKw(v)) this.unexpected(v);
  }
  private expectOp(v: string) {
    if (!this.acceptOp(v)) this.unexpected(v);
  }
  private name(what: string): string {
    const t = this.peek();
    if (t.t !== "id") return bad(`Nome inválido ${what}.`);
    this.i++;
    return t.v;
  }

  statement(): Select {
    if (!this.isKw("SELECT")) bad("Somente consultas SELECT (leitura) são aceitas.");
    this.i++;
    const distinct = this.acceptKw("DISTINCT");
    if (!distinct) this.acceptKw("ALL");
    const items: Item[] = [];
    do {
      items.push(this.item());
    } while (this.acceptOp(","));
    if (items.length > SQL_MAX_COLUMNS) bad(`Muitas colunas (máximo ${SQL_MAX_COLUMNS}).`);
    this.expectKw("FROM");
    const from = this.ref();
    const joins: Join[] = [];
    for (;;) {
      let left = false;
      if (this.acceptKw("LEFT")) {
        left = true;
        this.acceptKw("OUTER");
        this.expectKw("JOIN");
      } else if (this.acceptKw("INNER")) {
        this.expectKw("JOIN");
      } else if (!this.acceptKw("JOIN")) {
        break;
      }
      if (joins.length >= SQL_MAX_JOINS) bad(`No máximo ${SQL_MAX_JOINS} JOIN por consulta.`);
      const ref = this.ref();
      this.expectKw("ON");
      joins.push({ left, ref, on: this.expr() });
    }
    const s: Select = { distinct, items, from, joins, groupBy: [], orderBy: [] };
    if (this.acceptKw("WHERE")) s.where = this.expr();
    if (this.acceptKw("GROUP")) {
      this.expectKw("BY");
      do {
        s.groupBy.push(this.expr());
      } while (this.acceptOp(","));
    }
    if (this.acceptKw("HAVING")) s.having = this.expr();
    if (this.acceptKw("ORDER")) {
      this.expectKw("BY");
      do {
        const e = this.expr();
        const dir = this.acceptKw("ASC") ? "ASC" : this.acceptKw("DESC") ? "DESC" : undefined;
        s.orderBy.push(dir ? { e, dir } : { e });
      } while (this.acceptOp(","));
    }
    if (this.acceptKw("LIMIT")) {
      s.limit = this.int("LIMIT", Number.MAX_SAFE_INTEGER);
      if (this.acceptKw("OFFSET")) s.offset = this.int("OFFSET", SQL_MAX_OFFSET);
    }
    const semi = this.acceptOp(";");
    const t = this.peek();
    if (t.t !== "eof") {
      if (semi || (t.t === "kw" && ["UNION", "EXCEPT", "INTERSECT"].includes(t.v)))
        bad("Envie uma consulta só (sem ;, UNION, EXCEPT ou INTERSECT).");
      this.unexpected("o fim da consulta");
    }
    return s;
  }

  private int(what: string, max: number): number {
    const t = this.peek();
    if (t.t !== "num" || !/^[0-9]+$/.test(t.v)) return bad(`${what} precisa ser um inteiro não negativo.`);
    this.i++;
    const n = Number(t.v);
    if (n > max) bad(`${what} acima do máximo (${max}).`);
    return n;
  }

  private item(): Item {
    if (this.acceptOp("*")) return { k: "star" };
    if (this.peek().t === "id" && this.peek(1).v === "." && this.peek(2).v === "*") {
      const t = this.next().v;
      this.i += 2;
      return { k: "star", t };
    }
    const e = this.expr();
    if (this.acceptKw("AS")) return { k: "expr", e, alias: this.name("depois de AS") };
    if (this.peek().t === "id") return { k: "expr", e, alias: this.next().v };
    return { k: "expr", e };
  }

  private ref(): Ref {
    if (this.peek().t !== "id") bad("Nome de visão esperado depois de FROM/JOIN.");
    const view = this.next().v;
    if (this.acceptKw("AS")) return { view, alias: this.name("depois de AS") };
    if (this.peek().t === "id") return { view, alias: this.next().v };
    return { view, alias: view };
  }

  private nest<T>(fn: () => T): T {
    if (++this.depth > SQL_MAX_DEPTH) bad("Expressão aninhada demais.");
    try {
      return fn();
    } finally {
      this.depth--;
    }
  }

  expr(): Expr {
    return this.nest(() => {
      let l = this.and();
      while (this.acceptKw("OR")) l = { k: "bin", op: "OR", l, r: this.and() };
      return l;
    });
  }
  private and(): Expr {
    let l = this.not();
    while (this.acceptKw("AND")) l = { k: "bin", op: "AND", l, r: this.not() };
    return l;
  }
  private not(): Expr {
    if (this.acceptKw("NOT")) return this.nest(() => ({ k: "un", op: "NOT", e: this.not() }));
    return this.cmp();
  }
  private cmp(): Expr {
    const e = this.add();
    const t = this.peek();
    if (t.t === "op" && CMP.has(t.v)) {
      this.i++;
      return { k: "bin", op: t.v === "==" ? "=" : t.v, l: e, r: this.add() };
    }
    if (this.acceptKw("IS")) {
      const not = this.acceptKw("NOT");
      if (!this.acceptKw("NULL")) bad("IS só aceita NULL ou NOT NULL.");
      return { k: "isnull", e, not };
    }
    const not = this.isKw("NOT") && (this.isKw("IN", 1) || this.isKw("LIKE", 1) || this.isKw("BETWEEN", 1));
    if (not) this.i++;
    if (this.acceptKw("IN")) {
      this.expectOp("(");
      if (this.isKw("SELECT")) bad("Subconsulta não é aceita.");
      if (this.isOp(")")) bad("A lista do IN não pode ser vazia.");
      const list: Expr[] = [];
      do {
        list.push(this.add());
      } while (this.acceptOp(","));
      if (list.length > SQL_MAX_IN) bad(`No máximo ${SQL_MAX_IN} valores no IN.`);
      this.expectOp(")");
      return { k: "in", e, not, list };
    }
    if (this.acceptKw("LIKE")) return { k: "like", e, not, p: this.add() };
    if (this.acceptKw("BETWEEN")) {
      const lo = this.add();
      this.expectKw("AND");
      return { k: "between", e, not, lo, hi: this.add() };
    }
    return e;
  }
  private add(): Expr {
    let l = this.mul();
    for (;;) {
      const t = this.peek();
      if (t.t !== "op" || !["+", "-", "||"].includes(t.v)) return l;
      this.i++;
      l = { k: "bin", op: t.v, l, r: this.mul() };
    }
  }
  private mul(): Expr {
    let l = this.unary();
    for (;;) {
      const t = this.peek();
      if (t.t !== "op" || !["*", "/", "%"].includes(t.v)) return l;
      this.i++;
      l = { k: "bin", op: t.v, l, r: this.unary() };
    }
  }
  private unary(): Expr {
    const t = this.peek();
    if (t.t === "op" && (t.v === "-" || t.v === "+")) {
      this.i++;
      return this.nest(() => ({ k: "un", op: t.v as "-" | "+", e: this.unary() }));
    }
    return this.primary();
  }
  private primary(): Expr {
    const t = this.peek();
    if (t.t === "num") {
      this.i++;
      const n = Number(t.v);
      if (!Number.isFinite(n) || (/^[0-9]+$/.test(t.v) && !Number.isSafeInteger(n)))
        bad(`Número fora do alcance: ${t.v}`);
      return { k: "num", v: t.v };
    }
    if (t.t === "str") {
      this.i++;
      this.literals++;
      return { k: "str", v: t.v };
    }
    if (this.acceptKw("NULL")) return { k: "null" };
    if (this.acceptOp("(")) {
      if (this.isKw("SELECT")) bad("Subconsulta não é aceita.");
      const e = this.expr();
      this.expectOp(")");
      return { k: "paren", e };
    }
    if (this.acceptKw("CASE")) return this.caseExpr();
    if (this.acceptKw("CAST")) {
      this.expectOp("(");
      const e = this.expr();
      this.expectKw("AS");
      const ty = this.peek();
      const type = ty.v.toUpperCase();
      if (ty.t !== "id" || !CAST_TYPES.has(type)) bad("Tipo do CAST: INTEGER, REAL, TEXT ou NUMERIC.");
      this.i++;
      this.expectOp(")");
      return { k: "cast", e, type };
    }
    if (t.t === "id") {
      this.i++;
      if (this.acceptOp("(")) return this.call(t.v);
      if (this.acceptOp(".")) return { k: "col", t: t.v, c: this.name("depois do ponto") };
      return { k: "col", c: t.v };
    }
    // Palavra reservada usada como função (replace, exists...): fora da lista.
    if (t.t === "kw" && this.peek(1).v === "(") bad(`Função não permitida: ${t.v.toLowerCase()}`);
    return this.unexpected("uma expressão");
  }
  private caseExpr(): Expr {
    const base = this.isKw("WHEN") || this.isKw("END") ? undefined : this.expr();
    const whens: [Expr, Expr][] = [];
    while (this.acceptKw("WHEN")) {
      const w = this.expr();
      this.expectKw("THEN");
      whens.push([w, this.expr()]);
    }
    if (whens.length === 0) this.unexpected("WHEN");
    const els = this.acceptKw("ELSE") ? this.expr() : undefined;
    this.expectKw("END");
    return { k: "case", ...(base ? { base } : {}), whens, ...(els ? { els } : {}) };
  }
  private call(name: string): Expr {
    const def = Object.hasOwn(FUNCTIONS, name) ? FUNCTIONS[name] : undefined;
    if (!def) return bad(`Função não permitida: ${name}`);
    if (this.isKw("SELECT")) bad("Subconsulta não é aceita.");
    const distinct = this.acceptKw("DISTINCT");
    if (distinct && !def[2]) bad("DISTINCT só vale em funções de agregação.");
    if (this.acceptOp("*")) {
      if (name !== "count") bad("* só vale em count(*).");
      this.expectOp(")");
      return { k: "fn", name, distinct, star: true, args: [] };
    }
    const args: Expr[] = [];
    if (!this.isOp(")")) {
      do {
        args.push(this.expr());
      } while (this.acceptOp(","));
    }
    this.expectOp(")");
    if (args.length < def[0] || args.length > def[1] || (name === "count" && args.length === 0))
      bad(`Número de argumentos inválido em ${name}.`);
    return { k: "fn", name, distinct, star: false, args };
  }
}

// ---------- validação e reescrita ----------

const q = (name: string) => `"${name.replace(/"/g, '""')}"`;

/** Rótulo legível (sem aspas) da expressão, para nomear a coluna sem alias. */
function label(e: Expr): string {
  switch (e.k) {
    case "num":
      return e.v;
    case "str":
      return `'${e.v.replace(/'/g, "''")}'`;
    case "null":
      return "NULL";
    case "col":
      return e.t ? `${e.t}.${e.c}` : e.c;
    case "paren":
      return `(${label(e.e)})`;
    case "un":
      return e.op === "NOT" ? `NOT ${label(e.e)}` : `${e.op}${label(e.e)}`;
    case "bin":
      return `${label(e.l)} ${e.op} ${label(e.r)}`;
    case "isnull":
      return `${label(e.e)} IS ${e.not ? "NOT " : ""}NULL`;
    case "in":
      return `${label(e.e)} ${e.not ? "NOT " : ""}IN (${e.list.map(label).join(", ")})`;
    case "like":
      return `${label(e.e)} ${e.not ? "NOT " : ""}LIKE ${label(e.p)}`;
    case "between":
      return `${label(e.e)} ${e.not ? "NOT " : ""}BETWEEN ${label(e.lo)} AND ${label(e.hi)}`;
    case "case":
      return "CASE";
    case "cast":
      return `CAST(${label(e.e)} AS ${e.type})`;
    case "fn":
      return `${e.name}(${e.distinct ? "DISTINCT " : ""}${e.star ? "*" : e.args.map(label).join(", ")})`;
  }
}

type Scope = { refs: Ref[]; aliases: Set<string> | null };

class Compiler {
  params: (string | number)[] = [];
  constructor(private readonly catalog: SqlCatalog) {}

  view(name: string): SqlViewDef {
    const v = Object.hasOwn(this.catalog, name) ? this.catalog[name] : undefined;
    return v ?? bad(`Visão desconhecida: ${name}. Use uma das visões listadas.`);
  }

  /** Coluna resolvida: `"alias"."coluna"`, ou alias do SELECT (só em GROUP BY, HAVING e ORDER BY). */
  col(e: { t?: string; c: string }, s: Scope): string {
    if (e.t !== undefined) {
      const ref = s.refs.find((r) => r.alias === e.t) ?? bad(`Tabela desconhecida na consulta: ${e.t}`);
      if (!Object.hasOwn(this.view(ref.view).columns, e.c)) bad(`Coluna desconhecida: ${e.t}.${e.c}`);
      return `${q(ref.alias)}.${q(e.c)}`;
    }
    const owners = s.refs.filter((r) => Object.hasOwn(this.view(r.view).columns, e.c));
    if (owners.length > 1) bad(`Coluna ambígua: ${e.c}. Use apelido.coluna.`);
    if (owners.length === 1) return `${q(owners[0]!.alias)}.${q(e.c)}`;
    if (s.aliases?.has(e.c)) return q(e.c);
    return bad(`Coluna desconhecida: ${e.c}`);
  }

  expr(e: Expr, s: Scope): string {
    switch (e.k) {
      case "num":
        return e.v;
      case "str":
        this.params.push(e.v);
        return "?";
      case "null":
        return "NULL";
      case "col":
        return this.col(e, s);
      case "paren":
        return `(${this.expr(e.e, s)})`;
      case "un":
        return `(${e.op} ${this.expr(e.e, s)})`;
      case "bin":
        return `(${this.expr(e.l, s)} ${e.op} ${this.expr(e.r, s)})`;
      case "isnull":
        return `(${this.expr(e.e, s)} IS ${e.not ? "NOT " : ""}NULL)`;
      case "in": {
        const l = this.expr(e.e, s);
        return `(${l} ${e.not ? "NOT " : ""}IN (${e.list.map((x) => this.expr(x, s)).join(", ")}))`;
      }
      case "like": {
        const l = this.expr(e.e, s);
        return `(${l} ${e.not ? "NOT " : ""}LIKE ${this.expr(e.p, s)})`;
      }
      case "between": {
        const l = this.expr(e.e, s);
        const lo = this.expr(e.lo, s);
        return `(${l} ${e.not ? "NOT " : ""}BETWEEN ${lo} AND ${this.expr(e.hi, s)})`;
      }
      case "case": {
        let out = "CASE";
        if (e.base) out += ` ${this.expr(e.base, s)}`;
        for (const [w, t] of e.whens) {
          out += ` WHEN ${this.expr(w, s)}`;
          out += ` THEN ${this.expr(t, s)}`;
        }
        if (e.els) out += ` ELSE ${this.expr(e.els, s)}`;
        return `${out} END`;
      }
      case "cast":
        return `CAST(${this.expr(e.e, s)} AS ${e.type})`;
      case "fn":
        return `${e.name}(${e.distinct ? "DISTINCT " : ""}${e.star ? "*" : e.args.map((a) => this.expr(a, s)).join(", ")})`;
    }
  }

  ref(r: Ref): string {
    const v = this.view(r.view);
    this.params.push(...v.params);
    return `(${v.source}) AS ${q(r.alias)}`;
  }

  /**
   * Revisão de segurança T1.14 (produto quase cartesiano travava o DO). O ON precisa de uma igualdade entre a CHAVE
   * PRIMÁRIA (única) de uma visão e uma chave estrangeira declarada para ela na outra — uma delas a visão nova, a
   * outra uma anterior. Colunas de dimensão (dia, situação, vendedor...) não ligam visões; a própria visão só se liga
   * a ela mesma pelo id. Devolve se o JOIN é "um para muitos" (a visão nova é o lado muitos), que só vale uma vez.
   */
  checkJoin(on: Expr, joined: Ref, before: Ref[]): "one" | "many" {
    const conj: Expr[] = [];
    const walk = (e: Expr) => {
      if (e.k === "bin" && e.op === "AND") {
        walk(e.l);
        walk(e.r);
      } else conj.push(e.k === "paren" ? e.e : e);
    };
    walk(on);
    const all = [...before, joined];
    const owner = (e: Expr): { ref: Ref; col: string } | undefined => {
      if (e.k !== "col") return undefined;
      const hits = all.filter((r) =>
        e.t !== undefined ? r.alias === e.t : Object.hasOwn(this.view(r.view).columns, e.c),
      );
      return hits.length === 1 && Object.hasOwn(this.view(hits[0]!.view).columns, e.c)
        ? { ref: hits[0]!, col: e.c }
        : undefined;
    };
    const isPk = (x: { ref: Ref; col: string }) => this.view(x.ref.view).pk === x.col;
    /** `fk` aponta para a pk de `pk`: estrangeira declarada, ou a mesma visão ligada pelo id (1:1). */
    const refersTo = (fk: { ref: Ref; col: string }, pk: { ref: Ref; col: string }) => {
      const fks = this.view(fk.ref.view).fks ?? {};
      return (
        (Object.hasOwn(fks, fk.col) && fks[fk.col] === pk.ref.view) ||
        (fk.ref.view === pk.ref.view && isPk(fk))
      );
    };
    let kind: "one" | "many" | undefined;
    for (const c of conj) {
      if (c.k !== "bin" || c.op !== "=") continue;
      const a = owner(c.l);
      const b = owner(c.r);
      if (!a || !b || (a.ref === joined) === (b.ref === joined)) continue;
      const [n, o] = a.ref === joined ? [a, b] : [b, a];
      if (isPk(n) && refersTo(o, n)) kind = "one";
      else if (isPk(o) && refersTo(n, o)) kind ??= "many";
    }
    if (!kind) {
      const v = this.view(joined.view);
      const fks = Object.entries(v.fks ?? {}).map(([col, to]) => `${col} → ${to}.id`);
      return bad(
        `O JOIN precisa ligar uma chave estrangeira à chave primária (id) da outra visão com =` +
          ` (${joined.alias}: ${[v.pk ? `chave primária: ${v.pk}` : "", fks.length ? `estrangeiras: ${fks.join(", ")}` : ""].filter(Boolean).join("; ") || "esta visão não se liga a outras"}).`,
      );
    }
    return kind;
  }
}

export type CompileOpts = { defaultLimit?: number; maxLimit?: number };

/** Valida e reescreve a consulta. Erro de regra = DomainError `invalid_sql` com a mensagem para o modelo corrigir. */
export function compileReadonlySql(text: string, catalog: SqlCatalog, opts: CompileOpts = {}): CompiledSql {
  if (text.length > SQL_MAX_CHARS) bad(`Consulta grande demais (máximo ${SQL_MAX_CHARS} caracteres).`);
  const toks = tokenizeSql(text);
  if (toks.length > SQL_MAX_TOKENS) bad("Consulta grande demais.");
  const p = new Parser(toks);
  const s = p.statement();
  if (p.literals > SQL_MAX_LITERALS) bad(`No máximo ${SQL_MAX_LITERALS} literais de texto por consulta.`);

  const c = new Compiler(catalog);
  const refs = [s.from, ...s.joins.map((j) => j.ref)];
  const seen = new Set<string>();
  for (const r of refs) {
    c.view(r.view);
    if (seen.has(r.alias)) bad(`Apelido repetido: ${r.alias}`);
    seen.add(r.alias);
  }
  const kinds = s.joins.map((j, i) => c.checkJoin(j.on, j.ref, refs.slice(0, i + 1)));
  if (kinds.filter((k) => k === "many").length > 1)
    bad(
      "No máximo um JOIN do tipo um-para-muitos por consulta (ligue as demais visões pela chave primária delas).",
    );

  const base: Scope = { refs, aliases: null };
  const columns: string[] = [];
  const parts: string[] = [];
  for (const it of s.items) {
    if (it.k === "star") {
      const targets =
        it.t === undefined
          ? refs
          : [refs.find((r) => r.alias === it.t) ?? bad(`Tabela desconhecida na consulta: ${it.t}`)];
      for (const r of targets)
        for (const col of Object.keys(c.view(r.view).columns)) {
          parts.push(`${q(r.alias)}.${q(col)} AS ${q(col)}`);
          columns.push(col);
        }
    } else {
      const name = it.alias ?? (it.e.k === "col" ? it.e.c : label(it.e).slice(0, LABEL_MAX));
      parts.push(`${c.expr(it.e, base)} AS ${q(name)}`);
      columns.push(name);
    }
  }
  if (columns.length > SQL_MAX_COLUMNS) bad(`Muitas colunas (máximo ${SQL_MAX_COLUMNS}).`);
  const aliases: Scope = {
    refs,
    aliases: new Set(s.items.flatMap((it) => (it.k === "expr" && it.alias ? [it.alias] : []))),
  };

  let sql = `SELECT ${s.distinct ? "DISTINCT " : ""}${parts.join(", ")} FROM ${c.ref(s.from)}`;
  for (const j of s.joins) {
    const ref = c.ref(j.ref);
    sql += ` ${j.left ? "LEFT JOIN" : "JOIN"} ${ref} ON ${c.expr(j.on, base)}`;
  }
  if (s.where) sql += ` WHERE ${c.expr(s.where, base)}`;
  if (s.groupBy.length) sql += ` GROUP BY ${s.groupBy.map((e) => c.expr(e, aliases)).join(", ")}`;
  if (s.having) sql += ` HAVING ${c.expr(s.having, aliases)}`;
  if (s.orderBy.length)
    sql += ` ORDER BY ${s.orderBy.map((o) => `${c.expr(o.e, aliases)}${o.dir ? ` ${o.dir}` : ""}`).join(", ")}`;
  const max = opts.maxLimit ?? SQL_MAX_LIMIT;
  const limit = Math.min(s.limit ?? opts.defaultLimit ?? SQL_DEFAULT_LIMIT, max);
  sql += ` LIMIT ${limit}`;
  if (s.offset !== undefined) sql += ` OFFSET ${s.offset}`;
  if (c.params.length > MAX_PARAMS) bad("Consulta grande demais.");
  return { sql, params: c.params, columns, limit, views: [...new Set(refs.map((r) => r.view))] };
}

export type QueryPlanRow = { id: number; parent: number; detail: string };
const SUBPLAN =
  /^(MATERIALIZE|CO-ROUTINE|SUBQUERY|LIST SUBQUERY|CORRELATED|SCALAR SUBQUERY|MULTI-INDEX OR|COMPOUND)/;

/**
 * Defesa em profundidade (revisão T1.14) sobre o `EXPLAIN QUERY PLAN` da consulta já reescrita: no laço principal, mais
 * de uma varredura inteira (`SCAN`) significa uma visão inteira percorrida para cada linha de outra (SCAN × SCAN).
 * Varreduras dentro de subplanos (visão materializada uma vez, subconsulta correlacionada com índice) não contam.
 */
export function queryPlanProblem(rows: readonly QueryPlanRow[]): string | null {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const nested = (r: QueryPlanRow): boolean => {
    for (let p = byId.get(r.parent), n = 0; p && n < rows.length; p = byId.get(p.parent), n++)
      if (SUBPLAN.test(p.detail)) return true;
    return false;
  };
  const scans = rows.filter((r) => /^SCAN /.test(r.detail) && r.detail !== "SCAN CONSTANT ROW" && !nested(r));
  return scans.length > 1
    ? "Consulta pesada demais: ela percorreria uma visão inteira para cada linha de outra. Ligue as visões pela chave primária (id) ou filtre antes."
    : null;
}
